import type { SecondaryFactor } from '../types'

export type ErrorProps = {
  message: string
  title: string
  severity?: 'error' | 'warning' | 'info' | 'success'
}

export class UserFacingError extends Error {
  titleKey = 'global.error_title'
  messageKey = 'global.error_message'

  constructor(titleKey: string, messageKey: string) {
    super()
    this.titleKey = titleKey
    this.messageKey = messageKey
  }
}

export class MfaRequiredError extends Error {
  recipient: string
  channel: SecondaryFactor

  constructor(channel: SecondaryFactor, recipient: string) {
    super('missing_authentication_code')
    this.recipient = recipient
    this.channel = channel
  }
}

export class FailedAuthenticationError extends Error {}
export class TooManyAttemptsError extends Error {}

/**
 * BR activation — wrong PIN. Maps the frozen `422 { error: "invalid_credentials" }`
 * response (contract §7 `/pin`). Generic on purpose (anti-enumeration).
 */
export class InvalidPinError extends Error {}

/**
 * BR activation — any non-wrong PIN failure (blocked/inactive/backend-unreachable/
 * missing-identifier/no-usable-channel/unknown), collapsed server-side into a single
 * generic `422 { error: "pin_unavailable" }` (contract §7/§8). The underlying reason
 * is never exposed to the client; the FE shows one generic "try again / contact care"
 * red alert.
 */
export class PinUnavailableError extends Error {}

/**
 * BR activation — wrong OTP code. Maps `422 { error: "invalid_code" }`
 * returned by `/security_code/verify`, `/phone/verify`, `/email/verify`.
 */
export class InvalidCodeError extends Error {}

/**
 * BR activation — password policy failure. Maps
 * `422 { error: "password_policy", details: {...} }` from `/password`.
 */
export class PasswordPolicyError extends Error {
  constructor(public details?: Record<string, unknown>) {
    super('password_policy')
  }
}

/**
 * BR activation — phone number already linked to another account. Maps
 * `422 { error: "invalid_phone" }` returned by `/phone/check` when
 * `ActiveRecord::RecordInvalid` is raised (e.g. phone_number_usage_limit).
 * The user cannot self-serve; the FE redirects to FR care.
 */
export class InvalidPhoneError extends Error {}

/**
 * BR activation — the server expired/rejected the gating cookie. Maps the
 * `401` from `GET /state` (and any gated POST). The orchestrator redirects
 * back to `/signin` (contract §7).
 */
export class BrActivationSessionExpiredError extends Error {}
export class InvalidPasswordForPasswordChangeError extends Error {}
export class AccountDeletionCancellationRequiredError extends Error {
  constructor(
    public token: string,
    public request_uuid: string,
  ) {
    super()
  }
}

export enum ErrorCodes {
  EXPIRED_SESSION = 'EXPIRED_SESSION',
  INVALID_GRANT = 'invalid_grant',
  MISSING_AUTHENTICATION_CODE = 'missing_authentication_code',
  INVALID_TWO_STEP_VERIFICATION_CODE = 'invalid_two_step_verification_code',
  ACCOUNT_DELETION_CANCELLATION_REQUIRED = 'account_deletion_cancellation_required',
}
